import {
  formatearFecha,
  formatearFechaHora,
  type ResumenDePregunta,
  textoDeRespuesta,
} from "@gestiondoc/shared";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { useState } from "react";
import { queryClient, useTRPC } from "../api.ts";
import { Encabezado, Kpi } from "../componentes/Encabezado.tsx";
import { Modal } from "../componentes/ModalFirma.tsx";
import {
  Aviso,
  Boton,
  CABECERA,
  Enlace,
  EstadoVacio,
  Pestanas,
  Pildora,
  Tabla,
  Tarjeta,
  TD,
  TH,
} from "../componentes/ui.tsx";
import { mensajeDe } from "../utiles.ts";

type Vista = "resumen" | "personas" | "participacion";

export function PaginaResultadosEncuesta() {
  const { id } = useParams({ from: "/app/encuestas/$id/resultados" });
  const trpc = useTRPC();
  const resultados = useQuery(trpc.encuestas.resultados.queryOptions({ id }));
  const [vista, setVista] = useState<Vista>("resumen");
  const [cerrando, setCerrando] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const alTerminar = {
    onSuccess: () => queryClient.invalidateQueries(),
    onError: (e: unknown) => setError(mensajeDe(e)),
  };
  const cerrar = useMutation(
    trpc.encuestas.cerrar.mutationOptions({
      ...alTerminar,
      onSettled: () => setCerrando(false),
    }),
  );
  const recordar = useMutation(
    trpc.encuestas.recordar.mutationOptions({
      ...alTerminar,
      onSuccess: (r) => {
        setAviso(
          r.avisados === 1 ? "Se le avisó a 1 persona." : `Se les avisó a ${r.avisados} personas.`,
        );
        queryClient.invalidateQueries();
      },
    }),
  );

  if (resultados.error) return <EstadoVacio>{mensajeDe(resultados.error)}</EstadoVacio>;
  if (!resultados.data) return <EstadoVacio>Cargando…</EstadoVacio>;
  const r = resultados.data;
  const faltan = r.personas.filter((p) => !p.respondida);
  const avance = r.total ? Math.round((r.respondieron / r.total) * 100) : 0;
  const vistas: { clave: Vista; nombre: string }[] = [
    { clave: "resumen", nombre: "Resumen" },
    ...(r.anonima ? [] : [{ clave: "personas" as const, nombre: "Por persona" }]),
    { clave: "participacion", nombre: "Participación" },
  ];

  return (
    <div className="mx-auto max-w-[860px]">
      <Encabezado
        overline="Encuestas"
        titulo={r.titulo}
        acciones={
          <>
            {!r.retenidos && (
              <>
                <Enlace href={`/reportes/encuestas/${id}/resultados.xlsx`}>Exportar Excel</Enlace>
                <Enlace href={`/reportes/encuestas/${id}/resultados.pdf`}>Exportar PDF</Enlace>
              </>
            )}
            {r.abierta && faltan.length > 0 && (
              <Boton
                variante="secundario"
                disabled={recordar.isPending}
                onClick={() => recordar.mutate({ id })}
              >
                Recordar a quienes faltan
              </Boton>
            )}
            {r.abierta && (
              <Boton variante="peligro" onClick={() => setCerrando(true)}>
                Cerrar encuesta
              </Boton>
            )}
          </>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2 text-[12.5px] text-texto-3">
        <Pildora tono={r.abierta ? "bg-verde-fondo text-verde" : "bg-marino-claro text-marino"}>
          {r.abierta ? "Abierta" : "Cerrada"}
        </Pildora>
        {r.anonima && <Pildora tono="bg-gris-fondo text-gris">Anónima</Pildora>}
        {r.publicadaEn && <span>Publicada el {formatearFecha(r.publicadaEn)}</span>}
        {r.abierta && r.cierraEn && <span>· cierra el {formatearFecha(r.cierraEn)}</span>}
      </div>

      <div className="mb-5 grid gap-3 sm:grid-cols-3">
        <Kpi valor={`${r.respondieron} de ${r.total}`} etiqueta="Respondieron" />
        <Kpi
          valor={`${avance}%`}
          etiqueta="Participación"
          tono={avance >= 50 ? "verde" : "ambar"}
        />
        <Kpi valor={faltan.length} etiqueta="Faltan" tono={faltan.length ? "ambar" : "verde"} />
      </div>

      {error && <Aviso className="mb-4">{error}</Aviso>}
      {aviso && (
        <Aviso tono="ambar" className="mb-4">
          {aviso}
        </Aviso>
      )}

      <Pestanas opciones={vistas} activa={vista} onCambio={setVista} className="mb-4" />

      {vista === "resumen" &&
        (r.retenidos ? (
          <EstadoVacio>{r.retenidos}</EstadoVacio>
        ) : (
          <div className="space-y-4">
            {r.resumen.map((p, i) => (
              <TarjetaDeResumen key={p.id} numero={i + 1} pregunta={p} />
            ))}
          </div>
        ))}

      {vista === "personas" &&
        (r.individuales.length === 0 ? (
          <EstadoVacio>Todavía nadie ha respondido.</EstadoVacio>
        ) : (
          <div className="space-y-4">
            {r.individuales.map((f) => (
              <Tarjeta key={`${f.nombre}-${f.enviadaEn?.getTime()}`} titulo={f.nombre}>
                {f.enviadaEn && (
                  <p className="mb-2 text-[11.5px] text-texto-3">
                    {formatearFechaHora(f.enviadaEn)}
                  </p>
                )}
                <dl className="space-y-2">
                  {r.preguntas.map((p) => (
                    <div key={p.id}>
                      <dt className="text-[12px] font-semibold text-texto-2">{p.texto}</dt>
                      <dd className="text-[13px]">
                        {textoDeRespuesta(f.respuestas[p.id]) || (
                          <span className="text-texto-3">Sin responder</span>
                        )}
                      </dd>
                    </div>
                  ))}
                </dl>
              </Tarjeta>
            ))}
          </div>
        ))}

      {vista === "participacion" && (
        <Tabla minimo={480}>
          <thead>
            <tr className={CABECERA}>
              <th className={TH}>Persona</th>
              <th className={TH}>Puesto</th>
              <th className={TH}>Estado</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-borde/60">
            {r.personas.map((p) => (
              <tr key={`${p.nombre}-${p.puesto}`}>
                <td className={`${TD} font-semibold`}>{p.nombre}</td>
                <td className={TD}>{p.puesto}</td>
                <td className={TD}>
                  {p.respondida ? (
                    <Pildora tono="bg-verde-fondo text-verde">Respondió</Pildora>
                  ) : (
                    <Pildora tono="bg-ambar-fondo text-ambar">Falta</Pildora>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Tabla>
      )}

      {cerrando && (
        <Modal titulo="Cerrar la encuesta" onCerrar={() => setCerrando(false)}>
          <div className="space-y-4">
            <p className="text-[13px] text-texto-2">
              Ya nadie más podrá responder, y una encuesta cerrada no se vuelve a abrir.
              {faltan.length > 0 &&
                ` Faltan ${faltan.length === 1 ? "1 persona" : `${faltan.length} personas`}.`}
            </p>
            <div className="flex justify-end gap-2">
              <Boton variante="discreto" onClick={() => setCerrando(false)}>
                Cancelar
              </Boton>
              <Boton
                variante="peligro"
                disabled={cerrar.isPending}
                onClick={() => cerrar.mutate({ id })}
              >
                Cerrar encuesta
              </Boton>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function TarjetaDeResumen({
  numero,
  pregunta: p,
}: {
  numero: number;
  pregunta: ResumenDePregunta;
}) {
  return (
    <Tarjeta titulo={`${numero}. ${p.texto}`}>
      <p className="mb-3 text-[12px] text-texto-3">
        {p.contestaron === 1 ? "1 respuesta" : `${p.contestaron} respuestas`}
        {p.promedio !== null && ` · promedio ${p.promedio.toFixed(1)} de 5`}
      </p>
      {p.conteos.length > 0 && (
        <ul className="space-y-2">
          {p.conteos.map((c) => {
            const parte = p.contestaron ? Math.round((c.votos / p.contestaron) * 100) : 0;
            return (
              <li key={c.etiqueta}>
                <div className="mb-0.5 flex justify-between gap-3 text-[12.5px]">
                  <span>{c.etiqueta}</span>
                  <span className="shrink-0 text-texto-3">
                    {c.votos} · {parte}%
                  </span>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-gris-fondo">
                  <div className="h-full rounded-full bg-marino" style={{ width: `${parte}%` }} />
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {p.tipo === "texto" &&
        (p.textos.length === 0 ? (
          <p className="text-[13px] text-texto-3">Nadie escribió nada.</p>
        ) : (
          <ul className="space-y-1.5">
            {p.textos.map((t, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: dos personas pueden escribir lo mismo
              <li key={i} className="rounded-md bg-fondo px-3 py-2 text-[13px] whitespace-pre-line">
                {t}
              </li>
            ))}
          </ul>
        ))}
    </Tarjeta>
  );
}
